// src/auth/ForgotPassword.js
import React, { useState } from "react";
import usePageMeta from "../hooks/usePageMeta";
import { Link, useNavigate } from "react-router-dom";
import API from "../api";
import { showError, showSuccess } from "../utils/dialog";
import { isValidEmail } from "../utils/email";
import "../App.css";
import talentLogo2 from "../assets/talentlogo2.png";

function ForgotPassword() {
  usePageMeta("Forgot Password");
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resetting, setResetting] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      showError("Please enter a valid email address");
      return;
    }
    setSending(true);
    try {
      const res = await API.post("/auth/forgot-password", { email });
      showSuccess(res.data.message || "Verification code sent to your email.");
      setStep(2);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || "Something went wrong";
      showError(msg);
    } finally {
      setSending(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      showError("Please enter the 6-digit code from your email");
      return;
    }
    setVerifying(true);
    try {
      const res = await API.post("/auth/verify-reset-otp", { email, otp: code });
      if (res.data && res.data.valid) {
        showSuccess(res.data.message || "Code verified. You can now set a new password.");
        setStep(3);
      } else {
        showError(res.data?.message || "Invalid or expired code.");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || "Invalid or expired code.";
      showError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const code = otp.replace(/\D/g, "").slice(0, 6);
    if (!password || password.length < 8) {
      showError("Password must be at least 8 characters long");
      return;
    }
    if (password !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }
    setResetting(true);
    try {
      await API.post("/auth/reset-password-otp", {
        email,
        otp: code,
        password,
      });
      showSuccess("Password reset successful. You can now sign in.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || "Something went wrong";
      showError(msg);
    } finally {
      setResetting(false);
    }
  };

  const handleBackToEmail = () => {
    setStep(1);
    setOtp("");
    setPassword("");
    setConfirmPassword("");
  };

  const handleBackToOtp = () => {
    setStep(2);
    setPassword("");
    setConfirmPassword("");
  };

  return (
    <div className="auth-container">
      <div className="background-overlay"></div>
      <div className="form-container">
        <div className="form-card">
          <div className="atlas-logo">
            <img src={talentLogo2} alt="Talent Logo" className="h-14 w-auto" />
          </div>

          {step === 1 && (
            <>
              <h1 className="form-title">Password Reset</h1>
              <p style={{ textAlign: "center", fontSize: "0.875rem", color: "#6b7280", marginBottom: "1rem" }}>
                Enter the email address associated with your account. We&apos;ll send you a verification code.
              </p>

              <form className="auth-form" onSubmit={handleSendOtp}>
                <div className="form-group">
                  <label className="form-label">Email*</label>
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  style={{ marginTop: "0.5rem", letterSpacing: "0.05em" }}
                  disabled={sending}
                >
                  {sending ? "Sending..." : "SEND CODE"}
                </button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="form-title">Verify Code</h1>
              <p style={{ textAlign: "center", fontSize: "0.875rem", color: "#6b7280", marginBottom: "1rem" }}>
                Enter the 6-digit code we sent to <strong>{email}</strong>.
              </p>

              <form className="auth-form" onSubmit={handleVerifyOtp}>
                <div className="form-group">
                  <label className="form-label">Verification code*</label>
                  <input
                    type="text"
                    className="form-input"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  style={{ marginTop: "0.5rem", letterSpacing: "0.05em" }}
                  disabled={verifying}
                >
                  {verifying ? "Verifying..." : "VERIFY"}
                </button>
              </form>

              <p className="form-footer" style={{ marginTop: "0.5rem" }}>
                <button
                  type="button"
                  className="toggle-form"
                  onClick={handleBackToEmail}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}
                >
                  Use a different email
                </button>
              </p>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="form-title">Set New Password</h1>
              <p style={{ textAlign: "center", fontSize: "0.875rem", color: "#6b7280", marginBottom: "1rem" }}>
                Code verified. Enter your new password below.
              </p>

              <form className="auth-form" onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label className="form-label">New password*</label>
                  <div className="password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword((p) => !p)}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                  <span className="password-hint">Minimum 8 characters</span>
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm password*</label>
                  <div className="password-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowConfirmPassword((p) => !p)}
                    >
                      {showConfirmPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  style={{ marginTop: "0.5rem", letterSpacing: "0.05em" }}
                  disabled={resetting}
                >
                  {resetting ? "Resetting..." : "RESET PASSWORD"}
                </button>
              </form>

              <p className="form-footer" style={{ marginTop: "0.5rem" }}>
                <button
                  type="button"
                  className="toggle-form"
                  onClick={handleBackToOtp}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}
                >
                  Back to verification code
                </button>
              </p>
            </>
          )}

          <p className="form-footer" style={{ marginTop: "0.75rem" }}>
            <Link className="toggle-form" to="/login" style={{ color: "#18181b", fontWeight: 600 }}>
              BACK TO SIGN IN
            </Link>
          </p>

          <p className="form-footer">
            Don&apos;t have an account yet?{" "}
            <Link className="toggle-form" to="/signup">Sign up now</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
